/**
 * Builds the two cURL commands this app shows: the replay of a captured request
 * (moved out of EndpointView unchanged so the inspector and its unit tests share
 * one escaping implementation) and the starter request offered on an endpoint
 * that has captured nothing yet. The details pane renders the returned text
 * verbatim inside the terminal code surface.
 */
import type { Request } from '../types';
import { capturedSubPath } from './publicUrl';

/**
 * Escapes a value that callers place inside a single-quoted shell word.
 *
 * Single quotes suppress every expansion, so `"`, `\`, `$`, backticks and
 * newlines are delivered verbatim — adding a backslash to any of them would
 * change the bytes the server receives. The only byte that cannot appear inside
 * single quotes is the quote itself, which is escaped by closing the quoted
 * run, emitting an escaped quote and reopening.
 *
 * Shared by the replay command and the first-request starter snippet: both put
 * a URL and a body into a shell word, and neither may trust its input. The
 * endpoint slug, in particular, arrives from the address bar.
 */
export const escapeForShell = (str: string): string => str.replace(/'/g, "'\\''");

/**
 * The body the starter snippet POSTs: small enough to read at a glance, and
 * valid JSON so the captured row shows a parsed body as well as raw bytes.
 */
export const STARTER_REQUEST_BODY = '{"hello":"world"}';

/**
 * A ready-to-run first request for an endpoint that has captured nothing yet.
 *
 * `endpointUrl` is the *displayed* absolute endpoint URL (see
 * `buildEndpointUrl`), never the relative request base — the string is meant to
 * be pasted into a terminal, so a `/api/endpoint/<slug>` value here would be
 * unrunnable. Both the URL and the body go through the same single-quote
 * escaping as the replay command, so no user-controlled text can break out of
 * the quoting.
 */
export const generateStarterCurlCommand = (
  endpointUrl: string,
  body: string = STARTER_REQUEST_BODY,
): string =>
  [
    // Every line but the last ends in ` \`: the whole block is one shell
    // command, so a missing continuation would make the shell run the URL as a
    // program and curl would be left with no URL at all.
    'curl -X POST \\',
    `  '${escapeForShell(endpointUrl)}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  --data-raw '${escapeForShell(body)}'`,
  ].join('\n');

  export const generateCurlCommand = (request: Request, slug: string, apiUrl: string): string => {
  const endpointUrl = `${apiUrl}/endpoint/${slug}${capturedSubPath(request.path)}`;
  let curlCommand = `curl -X ${request.method}`;

  // Add headers (excluding host and some standard ones)
  const headersToSkip = ['host', 'content-length', 'connection', 'user-agent'];
  Object.entries(request.headers || {}).forEach(([key, value]) => {
    const keyLower = key.toLowerCase();
    if (!headersToSkip.includes(keyLower) && keyLower !== 'user-agent') {
      const escapedKey = escapeForShell(key);
      const escapedValue = escapeForShell(String(value));
      curlCommand += ` \\\n  -H '${escapedKey}: ${escapedValue}'`;
    }
  });

  // Add query parameters
  const queryString = Object.entries(request.queryParams || {})
    .map(([key, value]) => {
      const encodedKey = encodeURIComponent(key);
      const encodedValue = encodeURIComponent(String(value));
      return `${encodedKey}=${encodedValue}`;
    })
    .join('&');
  
  const url = queryString ? `${endpointUrl}?${queryString}` : endpointUrl;
  curlCommand += ` \\\n  '${url}'`;

  // Add body for POST/PUT/PATCH requests
  const bodyRaw = typeof request.bodyRaw === 'string' && request.bodyRaw !== '' ? request.bodyRaw : null;
  const hasBody =
    bodyRaw !== null ||
    (request.body !== undefined && request.body !== null && request.body !== '');

  if (hasBody && ['POST', 'PUT', 'PATCH'].includes(request.method)) {
    if (bodyRaw !== null) {
      // The bytes that arrived, replayed verbatim. `--data-raw` (not `--data`)
      // because `--data` strips newlines, which would change the payload.
      curlCommand += ` \\\n  --data-raw '${escapeForShell(bodyRaw)}'`;
    } else if (typeof request.body === 'string') {
      // Escape the body string
      const escapedBody = escapeForShell(request.body);
      curlCommand += ` \\\n  --data-raw '${escapedBody}'`;
    } else {
      // JSON body - need to escape properly
      const jsonBody = JSON.stringify(request.body);
      const escapedBody = escapeForShell(jsonBody);
      curlCommand += ` \\\n  -H 'Content-Type: application/json' \\\n  --data '${escapedBody}'`;
    }
  }

  return curlCommand;
};
